// src/services/pactService.js
import { supabase } from '../supabaseClient';

const toDateStr = (d = new Date()) => d.toISOString().split('T')[0]; // YYYY-MM-DD

// ─── INVITE MANAGEMENT ────────────────────────────────────────────────────────

export const lookupUserByUsername = async (username) => {
  const { data, error } = await supabase
    .from('user_stats')
    .select('user_id, username')
    .ilike('username', username.trim())
    .limit(1)
    .single();
  if (error) return null;
  return data;
};

const INVITE_BASE_URL = 'https://senditmaxx.com';

export const buildInviteUrl = (code) => `${INVITE_BASE_URL}/?pact=${code}`;

const generateInviteCode = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
};

export const createInvite = async ({ fromUserId, fromUsername, toUserId, toUsername }) => {
  const invite_code = generateInviteCode();
  const invite_url  = buildInviteUrl(invite_code);
  const { data, error } = await supabase
    .from('pact_invites')
    .insert([{
      from_user_id: fromUserId,
      from_username: fromUsername,
      to_user_id: toUserId || null,
      to_username: toUsername || null,
      invite_code,
      invite_url,
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const fetchMyPendingInvites = async (userId) => {
  const { data, error } = await supabase
    .from('pact_invites')
    .select('*')
    .eq('status', 'pending')
    .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`)
    .gt('expires_at', new Date().toISOString());
  if (error) throw error;
  return data || [];
};

export const fetchInviteByCode = async (code) => {
  const { data, error } = await supabase.rpc('get_pact_invite_by_code', {
    p_code: code.trim().toUpperCase(),
  });
  if (error) throw error;
  return data?.[0] || null;
};

export const acceptInvite = async (inviteId) => {
  const { data, error } = await supabase.rpc('accept_pact_invite', {
    p_invite_id: inviteId,
  });
  if (error) throw error;
  if (!data?.[0]) throw new Error('Invite acceptance did not create a pact.');
  return data[0];
};

export const declineInvite = async (inviteId) => {
  const { error } = await supabase.rpc('decline_pact_invite', {
    p_invite_id: inviteId,
  });
  if (error) throw error;
};

export const cancelInvite = async (inviteId) => {
  const { error } = await supabase.rpc('cancel_pact_invite', {
    p_invite_id: inviteId,
  });
  if (error) throw error;
};

// ─── PACT FETCHING ────────────────────────────────────────────────────────────

export const fetchActivePact = async (userId) => {
  const { data, error } = await supabase
    .from('pacts')
    .select('*')
    .eq('status', 'active')
    .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
    .single();
  if (error && error.code === 'PGRST116') return null;
  if (error) throw error;
  return data;
};

// ─── DAILY COMPLETIONS ────────────────────────────────────────────────────────

export const recordPactCompletion = async (pactId, userId) => {
  const today = toDateStr();
  const { error } = await supabase
    .from('pact_completions')
    .upsert([{ pact_id: pactId, user_id: userId, completion_date: today }], {
      onConflict: 'pact_id,user_id,completion_date',
    });
  if (error) throw error;
};

export const fetchTodayCompletions = async (pactId) => {
  const today = toDateStr();
  const { data, error } = await supabase
    .from('pact_completions')
    .select('user_id')
    .eq('pact_id', pactId)
    .eq('completion_date', today);
  if (error) throw error;
  return (data || []).map(r => r.user_id);
};

export const fetchCompletionsForDate = async (pactId, dateStr) => {
  const { data, error } = await supabase
    .from('pact_completions')
    .select('user_id')
    .eq('pact_id', pactId)
    .eq('completion_date', dateStr);
  if (error) throw error;
  return (data || []).map(r => r.user_id);
};

// ─── STREAK MANAGEMENT ────────────────────────────────────────────────────────

// Returns updated pact. Handles streak increment and gap detection.
export const syncPactStreak = async (pact, userId) => {
  const today = toDateStr();
  const yesterday = toDateStr(new Date(Date.now() - 86400000));

  const todayCompleted = await fetchTodayCompletions(pact.id);
  const bothDoneToday = todayCompleted.includes(pact.user1_id) && todayCompleted.includes(pact.user2_id);

  // Already counted today
  if (pact.last_completed_together === today) {
    return { pact, bitchOutUserId: null };
  }

  let newStreak = pact.streak;
  let newLastCompleted = pact.last_completed_together;
  let bitchOutUserId = null;

  if (bothDoneToday) {
    // Check if streak continues from yesterday or starts fresh
    if (!pact.last_completed_together || pact.last_completed_together === yesterday) {
      newStreak = (pact.streak || 0) + 1;
    } else {
      // Gap detected — streak resets and today starts at 1
      newStreak = 1;
      // Find who missed the gap day
      const gapCompleted = await fetchCompletionsForDate(pact.id, yesterday);
      if (!gapCompleted.includes(pact.user1_id)) bitchOutUserId = pact.user1_id;
      else if (!gapCompleted.includes(pact.user2_id)) bitchOutUserId = pact.user2_id;
      else bitchOutUserId = null; // both missed, pact streak reset only
    }
    newLastCompleted = today;

    const { data: updatedPact, error } = await supabase
      .from('pacts')
      .update({ streak: newStreak, last_completed_together: newLastCompleted })
      .eq('id', pact.id)
      .select()
      .single();
    if (error) throw error;
    return { pact: updatedPact, bitchOutUserId };
  }

  // Not both done today — check for a lapsed day (past midnight, yesterday wasn't completed together)
  if (
    pact.last_completed_together &&
    pact.last_completed_together < yesterday
  ) {
    // Streak has lapsed — find who failed yesterday
    const yesterdayCompleted = await fetchCompletionsForDate(pact.id, yesterday);
    if (!yesterdayCompleted.includes(pact.user1_id)) bitchOutUserId = pact.user1_id;
    else if (!yesterdayCompleted.includes(pact.user2_id)) bitchOutUserId = pact.user2_id;

    newStreak = 0;
    const { data: updatedPact, error } = await supabase
      .from('pacts')
      .update({ streak: newStreak })
      .eq('id', pact.id)
      .select()
      .single();
    if (error) throw error;

    if (bitchOutUserId) {
      await recordBitchOut(pact.id, bitchOutUserId, 'missed_daily');
    }

    return { pact: updatedPact, bitchOutUserId };
  }

  return { pact, bitchOutUserId: null };
};

// ─── SHAME NUDGES ─────────────────────────────────────────────────────────────

export const sendShame = async ({ pactId, fromUserId, fromUsername, toUserId }) => {
  const today = toDateStr();
  const { error } = await supabase
    .from('pact_shame_nudges')
    .upsert([{
      pact_id: pactId,
      from_user_id: fromUserId,
      from_username: fromUsername,
      to_user_id: toUserId,
      nudge_date: today,
      seen: false,
    }], { onConflict: 'pact_id,from_user_id,nudge_date' });
  if (error) throw error;
};

export const fetchUnseenShames = async (userId) => {
  const { data, error } = await supabase
    .from('pact_shame_nudges')
    .select('*')
    .eq('to_user_id', userId)
    .eq('seen', false)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const markShamesSeen = async (userId) => {
  const { error } = await supabase
    .from('pact_shame_nudges')
    .update({ seen: true })
    .eq('to_user_id', userId)
    .eq('seen', false);
  if (error) console.error('markShamesSeen error', error);
};

export const hasShamed = async (pactId, fromUserId) => {
  const today = toDateStr();
  const { data } = await supabase
    .from('pact_shame_nudges')
    .select('id')
    .eq('pact_id', pactId)
    .eq('from_user_id', fromUserId)
    .eq('nudge_date', today)
    .limit(1);
  return (data || []).length > 0;
};

// ─── BITCH OUTS ───────────────────────────────────────────────────────────────

export const recordBitchOut = async (pactId, userId, reason) => {
  const { error } = await supabase
    .from('pact_bitch_outs')
    .insert([{ pact_id: pactId, user_id: userId, reason }]);
  if (error) console.error('recordBitchOut error', error);
};

export const fetchUnseenBitchOuts = async (userId) => {
  // Fetch bitch outs from pacts where userId is the OTHER person (partner failed)
  const { data: pacts } = await supabase
    .from('pacts')
    .select('id, user1_id, user2_id')
    .or(`user1_id.eq.${userId},user2_id.eq.${userId}`);

  if (!pacts || pacts.length === 0) return [];

  const pactIds = pacts.map(p => p.id);
  const { data, error } = await supabase
    .from('pact_bitch_outs')
    .select('*')
    .in('pact_id', pactIds)
    .neq('user_id', userId)
    .eq('seen_by_partner', false)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const markBitchOutsSeen = async (userId) => {
  const { data: pacts } = await supabase
    .from('pacts')
    .select('id')
    .or(`user1_id.eq.${userId},user2_id.eq.${userId}`);

  if (!pacts || pacts.length === 0) return;
  const pactIds = pacts.map(p => p.id);

  await supabase
    .from('pact_bitch_outs')
    .update({ seen_by_partner: true })
    .in('pact_id', pactIds)
    .neq('user_id', userId)
    .eq('seen_by_partner', false);
};

// ─── DISSOLVE PACT ────────────────────────────────────────────────────────────

export const dissolvePact = async (pact, dissolvedByUserId) => {
  const { error } = await supabase
    .from('pacts')
    .update({
      status: 'dissolved',
      dissolved_by: dissolvedByUserId,
      dissolved_at: new Date().toISOString(),
    })
    .eq('id', pact.id);
  if (error) throw error;

  // Both get a bitch out for dissolving
  await Promise.all([
    recordBitchOut(pact.id, pact.user1_id, 'dissolved'),
    recordBitchOut(pact.id, pact.user2_id, 'dissolved'),
  ]);
};
